import { AppMark } from "../components/AppMark";
import { FacilityReadiness } from "../components/trim-room/FacilityReadiness";
import { BagAssignment, type AssignedBag } from "../components/trim-room/BagAssignment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/Toast";
import { useUnits } from "../hooks/useUnits";
import { fmtRate, fmtWeight, parseWeightInput, weightToInput, type UnitSystem } from "../lib/units";
import { ChipGrid, type ChipItem } from "../components/ChipGrid";
import { HygieneModal } from "../components/HygieneModal";
import { StopModal } from "../components/StopModal";
import { Modal } from "../components/Modal";
import { PhotoSlot, emptyPhotoField, type PhotoField } from "../components/PhotoSlot";
import { ScaleCamOverlay } from "../components/ScaleCamOverlay";
import {
  clearFlowPhotoIds, getDeviceId, getFlowPhotoIds, getLastStrainId, getNugSize, getTrimType,
  setFlowPhotoId, setLastStrainId, setNugSize, setTrimType, takePendingMode,
} from "../lib/storage";
import { linkFullBatchPhotos, queueRunOffline, queuePhotoOffline } from "../lib/sync";
import { listQueuedPhotos } from "../lib/outbox";
import { putPhotoOnce } from "../lib/photos";
import { uuid } from "../lib/uuid";
import { getDeviceSite } from "../lib/site";
import {
  NUG_SIZES, TRIM_TYPES, type Job, type LiveSession, type Strain, type WorkType,
} from "../lib/types";
import {
  ENGAGE_ME_KEY, celebrationTier, comparePb, useEngageMe,
  type CelebrationTier, type PbVerdict,  formatMoney,
} from "../lib/engage";
import { clockIn, clockOut, useLineStations, type LineStation, useLineState } from "../lib/line";
import { ConfettiBurst } from "../components/ConfettiBurst";

// Trim flow — mirrors legacy static/js/workflow.js's state machine. Quick
// mode: strain -> trim -> endweight -> done (M5a). Full-batch mode (M5c)
// inserts a start-weight step before trim and a QA-photo step after
// endweight: strain -> startweight -> trim -> endweight -> qa -> done. Kept
// as one page/component like the legacy single-page workflow.html rather
// than one route per step, because the timer/live-session state needs to
// survive step transitions and a route-per-step buys nothing here.

const RESUME_WINDOW_MS = 15 * 60 * 1000;
const RESUME_WARN_MS = 2 * 60 * 1000;
const HEARTBEAT_MS = 10000;

type Step = "strain" | "startweight" | "trim" | "endweight" | "qa" | "done";
type Mode = "quick" | "full";
type StrainPick = { id: number | null; name: string };

type Flow = {
  mode: Mode;
  clientUuid: string;
  bag: AssignedBag | null;
  strain: StrainPick | null;
  trimType: string;
  nugSize: string;
  jobId: number | null;
  // M6 wave-2 line mode: which station (if any) this run is clocked into.
  // Reset to null whenever a fresh flow starts (initialFlow) or a different
  // job is picked — resuming a station across a page reload isn't
  // supported, same pre-existing gap as jobId (see PROGRESS.md's
  // "applyEvent ignores job_id" note; live sessions don't carry either).
  stationId: number | null;
  stationName: string | null;
  stationIsFinal: boolean;
  liveId: number | null;
  startedAt: number;
  stoppedAt: number;
  pausedMs: number;
  pauseStart: number;
  paused: boolean;
  activeMs: number;
  startGrams: number | null;
  startPhoto: PhotoField;
  endGrams: number | null;
  endPhoto: PhotoField;
  qaPhoto: PhotoField;
  qaSkipped: boolean;
  stopPendingAt: number;
  finalized: boolean;
};

type WakeLockLike = { release(): Promise<void> };
// M6: celebration fields are all derived CLIENT-SIDE at save time from the
// engage/me snapshot cached since trim start (see lib/engage.ts's file
// banner) — never re-derived later, so the done screen can't flicker if
// that snapshot refetches in the background.
type DoneInfo = {
  grams: number | null; gpm: number | null; yieldPct: number | null; queued: boolean; job: Job | null;
  tier: CelebrationTier; verdict: PbVerdict; qaClean: boolean;
  todayGramsAfter: number | null; targetGrams: number | null; targetAfterPct: number | null;
  percentileBand: string | null;
  earnedCents: number | null; currency: string | null;
};
type PhotoWhich = "start" | "end" | "qa";

function initialFlow(mode: Mode): Flow {
  return {
    mode,
    clientUuid: uuid(),
    bag: null,
    strain: null,
    trimType: getTrimType() || "dry",
    nugSize: getNugSize() || "medium",
    jobId: null,
    stationId: null,
    stationName: null,
    stationIsFinal: false,
    liveId: null,
    startedAt: 0,
    stoppedAt: 0,
    pausedMs: 0,
    pauseStart: 0,
    paused: false,
    activeMs: 0,
    startGrams: null,
    startPhoto: emptyPhotoField(),
    endGrams: null,
    endPhoto: emptyPhotoField(),
    qaPhoto: emptyPhotoField(),
    qaSkipped: false,
    stopPendingAt: 0,
    finalized: false,
  };
}

function formatTimer(ms: number): string {
  const s = ms / 1000;
  const m = Math.floor(s / 60);
  const rem = (s % 60).toFixed(1);
  return `${String(m).padStart(2, "0")}:${rem.padStart(4, "0")}`;
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

function photoKey(which: PhotoWhich): "startPhoto" | "endPhoto" | "qaPhoto" {
  return which === "start" ? "startPhoto" : which === "end" ? "endPhoto" : "qaPhoto";
}

// M6 done-screen tiered copy — exact wording per the addictive-layer spec
// (docs/superpowers/specs/2026-08-30-ts-transition-spec.md): PB beaten >
// beat own average > solid finish. isPbGrams can be true with gpm === null
// (zero active_ms edge case), so that combination gets its own line rather
// than silently falling through to the solid-finish copy.
function celebrationCopy(info: DoneInfo, trimType: string, nugSize: string, system: UnitSystem): string {
  const style = trimType.replace("_", " ");
  if (info.tier === "pb") {
    if (info.verdict.gpm != null) {
      return `New personal best — ${fmtRate(info.verdict.gpm, system, 1)} on ${style}/${nugSize}`;
    }
    return `New personal best — ${fmtWeight(info.grams ?? 0, system, 1)} on ${style}/${nugSize}`;
  }
  if (info.tier === "avg" && info.verdict.vsAvgPct != null) {
    return `▲ ${info.verdict.vsAvgPct}% vs your average`;
  }
  return `${system === "imperial" ? fmtWeight(info.grams ?? 0, system) : `${(info.grams ?? 0).toFixed(1)} g`} banked`;
}

async function requestWakeLock(): Promise<WakeLockLike | null> {
  try {
    const nav = navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<WakeLockLike> } };
    if (!nav.wakeLock) return null;
    return await nav.wakeLock.request("screen");
  } catch {
    return null;
  }
}

export function TrimPage() {
  const { player, orgId } = useAuth();
  const units = useUnits();
  // Imperial entry: keep what the worker typed (oz) so "2." / "2.50" survive the
  // grams round-trip; the flow itself always holds grams.
  const [ozText, setOzText] = useState<{ start: string; end: string }>({ start: "", end: "" });
  const entryValue = (text: string, g: number | null | undefined): string | number => {
    if (g == null) return "";
    if (units !== "imperial") return g;
    return text !== "" && Math.abs(round(parseWeightInput(text, units), 2) - g) < 0.011 ? text : weightToInput(g, units);
  };
  const entryChange = (which: "start" | "end", raw: string) => {
    const grams = raw === "" ? null : units === "imperial" ? round(parseWeightInput(raw, units), 2) : parseFloat(raw);
    if (units === "imperial") setOzText((t) => ({ ...t, [which]: raw }));
    patch(which === "start" ? { startGrams: grams } : { endGrams: grams });
  };
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const deviceId = useMemo(() => getDeviceId(), []);

  const flowKey = `trim:bag-flow:${orgId}:${player?.id}`;
  const [restored] = useState<{ flow: Flow; step: Step } | null>(() => {
    try { const saved = JSON.parse(localStorage.getItem(flowKey) || 'null'); return saved?.flow?.bag && saved.step !== 'done' ? saved : null; } catch { return null; }
  });
  const [step, setStep] = useState<Step>(restored?.step || "strain");
  const [flow, setFlow] = useState<Flow>(() => restored ? { ...restored.flow, finalized: false } : initialFlow(takePendingMode()));
  const patch = (p: Partial<Flow>) => setFlow((f) => ({ ...f, ...p }));
  const patchPhoto = (which: PhotoWhich, p: Partial<PhotoField>) =>
    setFlow((f) => {
      if (which === "start") return { ...f, startPhoto: { ...f.startPhoto, ...p } };
      if (which === "end") return { ...f, endPhoto: { ...f.endPhoto, ...p } };
      return { ...f, qaPhoto: { ...f.qaPhoto, ...p } };
    });

  // Async handlers (heartbeat, stop, save) fire after awaits, so they read
  // the LATEST flow via this ref instead of a closed-over `flow` value that
  // may already be stale by the time the awaited call resolves.
  const flowRef = useRef(flow);
  useEffect(() => {
    flowRef.current = flow;
  }, [flow]);

  useEffect(() => {
    if (flow.bag && step !== 'done') localStorage.setItem(flowKey, JSON.stringify({ step, flow }));
    else localStorage.removeItem(flowKey);
  }, [flow, step, flowKey]);
  const [hygieneOpen, setHygieneOpen] = useState(false);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [scaleCamOpen, setScaleCamOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [saving, setSaving] = useState(false);
  const [doneInfo, setDoneInfo] = useState<DoneInfo | null>(null);

  const heartbeatRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const wakeLockRef = useRef<WakeLockLike | null>(null);
  const resumeAttemptedRef = useRef(false);

  const { data: strains } = useQuery({
    queryKey: ["strains"],
    queryFn: () => api.get("/api/strains").then((r) => readJson<Strain[]>(r)),
  });
  // The room's own work types. Falls back to the five built-ins if the call
  // fails (offline, or an org predating work types) so the strain step is
  // never left with nothing to tap.
  const { data: workTypes } = useQuery({
    queryKey: ["work-types"],
    queryFn: () => api.get("/api/work-types").then((r) => readJson<WorkType[]>(r)),
    retry: false,
  });
  const { data: jobs } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get("/api/jobs").then((r) => readJson<Job[]>(r)),
    retry: false,
  });
  const openJobs = (jobs || []).filter((j) => j.status === "open");

  // M6 wave-2 line mode: stations for the currently-picked job (disabled/
  // silently-absent with no job picked, or a job that never got any
  // configured — see lib/line.ts's file banner for the feature-degrade
  // contract). `isFetched` (not just `data`) gates the auto-advance effect
  // below so it can tell "no stations" apart from "haven't checked yet".
  const lineStations = useLineStations(flow.jobId);
  const needsStation = flow.jobId !== null && (lineStations.data?.length ?? 0) > 0;
  const [stationSwitchOpen, setStationSwitchOpen] = useState(false);
  // Pooled piece-rate view for the worker's own station chip/switch modal —
  // only while clocked in, and only meaningful when `pool` is non-null
  // (owner set a rate). Absent/null renders nothing, like everything else
  // in lib/line.ts.
  const lineState = useLineState(flow.jobId, { enabled: flow.stationId !== null, refetchInterval: 30_000 });
  const pool = lineState.data?.pool ?? null;
  const myShare = pool?.shares.find((sh) => sh.player_id === player?.id) ?? null;

  // M6: fetched once at mount (i.e. at trim start) and left alone for the
  // rest of the page's life — see lib/engage.ts's file banner for why this
  // snapshot, not a live refetch, is what the done-screen verdict compares
  // against. Feature-degrades silently: `engageMe` undefined (404/offline/
  // not-yet-deployed) just means no pace pill / no celebration extras below.
  const { data: engageMe } = useEngageMe();
  const [showConfetti, setShowConfetti] = useState(false);

  const stepNumbers = flow.mode === "full"
    ? { strain: 1, startweight: 2, trim: 3, endweight: 4, qa: 5 }
    : { strain: 1, trim: 2, endweight: 3 };

  // M6 peak-end celebration: fires once per doneInfo (i.e. once per save),
  // never on a re-render — solid finishes get a short buzz and no confetti,
  // avg/PB tiers get a bigger buzz plus a burst sized to the tier.
  useEffect(() => {
    if (step !== "done" || !doneInfo) return;
    const HAPTIC: Record<CelebrationTier, number[]> = {
      pb: [40, 30, 40, 30, 90],
      avg: [30, 20, 40],
      solid: [15],
    };
    if (navigator.vibrate) navigator.vibrate(HAPTIC[doneInfo.tier]);
    if (doneInfo.tier !== "solid") setShowConfetti(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, doneInfo]);

  // Pre-select (but don't auto-start) the last strain this device used in
  // this org — matches legacy's LAST_STRAIN localStorage pre-select. Only
  // applies at a fresh strain pick; the live-session resume effect below
  // always wins if there's an in-progress trim to pick back up.
  useEffect(() => {
    if (flow.strain || !strains || orgId === null) return;
    const lastId = getLastStrainId(orgId);
    if (lastId === null) return;
    const match = strains.find((s) => s.id === lastId);
    if (match) patch({ strain: { id: match.id, name: match.name } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strains, orgId]);

  // M6 wave-2: leaves the strain step once a strain is picked AND (when the
  // job needs one) a station is picked — order-independent, since this
  // re-checks on every relevant change rather than being driven from
  // whichever pick happened last. Declarative on purpose: pickJob sets
  // jobId+strain in the same event when a job carries a preset strain, and
  // reading flow.jobId synchronously right after that patch (the old
  // pickStrain-calls-itself approach) would see the PRE-update value —
  // React state updates aren't visible until the next render. An effect
  // watching the committed state sidesteps that stale-closure trap entirely.
  const advancedPastStrainRef = useRef(false);
  useEffect(() => {
    if (step !== "strain") {
      advancedPastStrainRef.current = false;
      return;
    }
    if (!flow.strain) return;
    if (flow.jobId !== null && !lineStations.isFetched) return; // still checking whether this job needs a station
    if (needsStation && !flow.stationId) return;
    if (advancedPastStrainRef.current) return;
    advancedPastStrainRef.current = true;
    const t = setTimeout(() => {
      if (flow.mode === "full") setStep("startweight");
      else setHygieneOpen(true);
    }, 160);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, flow.strain, flow.jobId, flow.stationId, flow.mode, lineStations.isFetched, needsStation]);

  function startTicking() {
    stopTicking();
    tickRef.current = window.setInterval(() => setNow(Date.now()), 100);
  }
  function stopTicking() {
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }
  function startHeartbeat() {
    stopHeartbeat();
    heartbeatRef.current = window.setInterval(sendHeartbeat, HEARTBEAT_MS);
  }
  function stopHeartbeat() {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }
  async function grabWake() {
    wakeLockRef.current = await requestWakeLock();
  }
  function releaseWake() {
    wakeLockRef.current?.release().catch(() => {});
    wakeLockRef.current = null;
  }

  async function sendHeartbeat() {
    const f = flowRef.current;
    if (!f.liveId || f.finalized) return;
    const pausedMs = f.paused ? f.pausedMs + (Date.now() - f.pauseStart) : f.pausedMs;
    try {
      const res = await api.postJson(`/api/live/${f.liveId}/heartbeat`, { paused: f.paused ? 1 : 0, paused_ms: pausedMs });
      const j = await res.json().catch(() => ({}));
      if (j.cancelled) onCancelledByAdmin(j.cancelled_by);
    } catch {
      /* best-effort — the outbox, not this heartbeat, is what makes runs offline-safe */
    }
  }

  function onCancelledByAdmin(by: string) {
    stopHeartbeat();
    stopTicking();
    releaseWake();
    setScaleCamOpen(false);
    if (flowRef.current.stationId !== null) clockOut();
    toast(`This session was cancelled${by === "admin" ? " by an admin" : ""}.`, "err");
    navigate({ to: "/" });
  }

  // Resume an in-progress live session on mount instead of legacy's ?resume=1
  // query flag — one GET /api/live/mine covers "did I leave mid-trim" for
  // both a fresh page load and a genuine app-restart resume. Also restores
  // mode/start weight/photo ids the pending-mode hand-off and initial render
  // can't know about (the live session is the source of truth once one
  // exists — see storage.ts's getFlowPhotoIds for why photo ids need their
  // own local cache instead of coming back off the live session row).
  useEffect(() => {
    if (resumeAttemptedRef.current) return;
    resumeAttemptedRef.current = true;
    if (restored?.flow.bag && restored.step === "trim") { startTicking(); grabWake(); }
    if (restored?.flow.bag) { if (navigator.onLine && restored.flow.liveId) startHeartbeat(); return; }
    (async () => {
      try {
        const res = await api.get("/api/live/mine");
        if (!res.ok) return;
        const { live } = await readJson<{ live: LiveSession | null }>(res);
        if (!live) return;
        const stopPendingAt = live.stop_pending_at || 0;
        const mode: Mode = live.mode === "full" ? "full" : "quick";
        const clientUuid = live.client_uuid || uuid();
        const cachedIds = getFlowPhotoIds(clientUuid);
        const pendingIds = new Set((await listQueuedPhotos()).map((p) => p.photo_id));
        const restorePhoto = (id: string | undefined): PhotoField =>
          id ? { id, status: pendingIds.has(id) ? "queued" : "done", previewUrl: null } : emptyPhotoField();
        patch({
          mode,
          liveId: live.id,
          bag: live.bag ?? null,
          clientUuid,
          startedAt: live.started_at,
          pausedMs: live.paused_ms || 0,
          paused: Boolean(live.paused),
          pauseStart: live.paused ? Date.now() : 0,
          strain: live.strain_id ? { id: live.strain_id, name: live.strain_name || "" } : null,
          startGrams: live.start_grams ?? null,
          startPhoto: restorePhoto(cachedIds.start),
          endPhoto: restorePhoto(cachedIds.end),
          qaPhoto: restorePhoto(cachedIds.qa),
          stopPendingAt,
          stoppedAt: stopPendingAt,
          activeMs: stopPendingAt ? Math.max(0, stopPendingAt - live.started_at - (live.paused_ms || 0)) : 0,
        });
        startHeartbeat();
        if (stopPendingAt) {
          setStep("endweight");
        } else {
          setStep("trim");
          startTicking();
          grabWake();
        }
        toast("Session resumed", "ok");
      } catch {
        /* offline or no resumable session — proceed with a fresh strain pick */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      stopTicking();
      stopHeartbeat();
      releaseWake();
    },
    [],
  );

  // ── Strain step ── (advancing past this step is handled by the effect
  // above, not here — see its comment for why).
  function pickStrain(s: StrainPick) {
    patch({ strain: s });
    if (s.id !== null && orgId !== null) setLastStrainId(orgId, s.id);
    if (navigator.vibrate) navigator.vibrate(10);
  }

  function pickJob(job: Job) {
    patch({
      jobId: job.id, trimType: job.trim_type || flow.trimType, nugSize: job.nug_size || flow.nugSize,
      stationId: null, stationName: null, stationIsFinal: false,
    });
    if (job.trim_type) setTrimType(job.trim_type);
    if (job.nug_size) setNugSize(job.nug_size);
    if (job.strain_id) {
      toast(`Trimming toward "${job.name}"`, "ok");
      pickStrain({ id: job.strain_id, name: job.strain_name || "" });
    } else {
      toast(`Job "${job.name}" selected — now pick the strain`, "ok");
    }
  }

  // ── Line-mode station picking ──
  // Clock-in is fire-and-forget against the local UI state, same convention
  // TrimPage already uses for /api/live/start (beginTrim below): the local
  // flow updates immediately so the picker feels instant, and a failed
  // network call just gets a toast rather than blocking progress — the
  // worker can retry from the (still-open) picker.
  async function switchToStation(s: LineStation) {
    if (navigator.vibrate) navigator.vibrate(10);
    const downgrading = flow.mode === "full" && !s.is_final;
    patch({ stationId: s.id, stationName: s.name, stationIsFinal: s.is_final });
    if (downgrading) {
      patch({ mode: "quick" });
      toast("Mid-line stations log quick-style — weights land at weigh-out", "ok");
    }
    const jobId = flow.jobId;
    if (jobId === null) return;
    try {
      await clockIn(jobId, s.id);
    } catch {
      toast("Could not clock in — try tapping the station again", "err");
    }
  }

  function addOtherStrain() {
    const name = (prompt("Strain name:") || "").trim();
    if (!name) return;
    toast(`Using new strain "${name}"`, "ok");
    pickStrain({ id: null, name });
  }

  // ── Photo capture (full-batch only) ──
  // One whole-file PUT attempt while online; anything else (offline, a
  // failed attempt, a non-committing response) falls back to the same
  // background retry queue used for offline runs — see lib/sync.ts's
  // flushPhotoOutbox / flushOutbox ordering.
  async function capturePhoto(which: PhotoWhich, file: File) {
    const photoId = uuid();
    const previewUrl = URL.createObjectURL(file);
    const contentType = file.type || "image/jpeg";
    patchPhoto(which, { id: photoId, status: "uploading", previewUrl });
    setFlowPhotoId(flowRef.current.clientUuid, which, photoId);

    if (!navigator.onLine) {
      await queuePhotoOffline(photoId, which, file, contentType, deviceId);
      patchPhoto(which, { status: "queued" });
      return;
    }
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const result = await putPhotoOnce(photoId, bytes, contentType, which, deviceId);
      if (result.committed) {
        patchPhoto(which, { status: "done" });
      } else {
        await queuePhotoOffline(photoId, which, file, contentType, deviceId);
        patchPhoto(which, { status: "queued" });
      }
    } catch {
      await queuePhotoOffline(photoId, which, file, contentType, deviceId);
      patchPhoto(which, { status: "queued" });
    }
  }

  function retryPhoto(which: PhotoWhich) {
    const field = flowRef.current[photoKey(which)];
    if (field.previewUrl) URL.revokeObjectURL(field.previewUrl);
    patchPhoto(which, emptyPhotoField());
  }

  async function beginTrim() {
    setHygieneOpen(false);
    const startedAt = Date.now();
    patch({ startedAt, pausedMs: 0, pauseStart: 0, paused: false, stopPendingAt: 0, stoppedAt: 0, finalized: false });
    setStep("trim");
    startTicking();
    grabWake();
    try {
      const f = flowRef.current;
      const res = await api.postJson("/api/live/start", {
        client_uuid: f.clientUuid,
        bag_id: f.bag?.id ?? null,
        strain_id: f.strain?.id ?? null,
        strain: f.strain?.id ? null : f.strain?.name ?? null,
        mode: f.mode,
        device_id: deviceId,
        start_grams: f.mode === "full" ? f.startGrams ?? null : null,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok && f.bag && res.status < 500) {
        stopTicking(); releaseWake(); patch({ startedAt: 0, bag: null, strain: null }); setStep('strain');
        toast(body.error || 'Assignment could not be verified', 'err'); return;
      }
      if (body?.live?.id) patch({ liveId: body.live.id });
    } catch {
      /* offline at start time — the timer still runs, just with no live-session id */
    }
    startHeartbeat();
  }

  function togglePause() {
    setFlow((f) => {
      if (!f.paused) return { ...f, paused: true, pauseStart: Date.now() };
      return { ...f, paused: false, pausedMs: f.pausedMs + (Date.now() - f.pauseStart), pauseStart: 0 };
    });
  }

  async function confirmStop() {
    setStopModalOpen(false);
    const stoppedAt = Date.now();
    setFlow((f) => {
      const pausedMs = f.paused ? f.pausedMs + (stoppedAt - f.pauseStart) : f.pausedMs;
      const activeMs = Math.max(0, stoppedAt - f.startedAt - pausedMs);
      return { ...f, stoppedAt, paused: false, pausedMs, activeMs, stopPendingAt: stoppedAt };
    });
    stopTicking();
    releaseWake();
    const liveId = flowRef.current.liveId;
    if (liveId) {
      try {
        const res = await api.postJson(`/api/live/${liveId}/stop`, {});
        const body = await res.json().catch(() => ({}));
        if (res.ok && body.live) {
          patch({ stopPendingAt: body.live.stop_pending_at || stoppedAt, pausedMs: body.live.paused_ms ?? flowRef.current.pausedMs });
        }
      } catch {
        /* best-effort — local stopPendingAt still governs the resume window */
      }
    }
    setStep("endweight");
  }

  function resumeLeftMs(): number {
    return flow.stopPendingAt ? Math.max(0, flow.stopPendingAt + RESUME_WINDOW_MS - now) : 0;
  }

  async function resumeTrimming() {
    if (!flowRef.current.stopPendingAt || resumeLeftMs() <= 0) return;
    const liveId = flowRef.current.liveId;
    if (liveId) {
      try {
        const res = await api.postJson(`/api/live/${liveId}/resume`, {});
        if (res.status === 410) {
          patch({ finalized: true });
          toast("Too late — the 15 minute window has passed", "warn");
          return;
        }
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast(body.error || "Resume failed", "err");
          return;
        }
        patch({ pausedMs: body.live?.paused_ms ?? flowRef.current.pausedMs });
      } catch {
        toast("Network error — try again", "err");
        return;
      }
    } else {
      patch({ pausedMs: flowRef.current.pausedMs + (Date.now() - flowRef.current.stopPendingAt) });
    }
    patch({ stopPendingAt: 0, stoppedAt: 0, activeMs: 0 });
    setStep("trim");
    startTicking();
    grabWake();
    toast("Back on the clock — same session", "ok");
  }

  // ── End-weight step transitions (full mode detours through QA first) ──
  function afterEndWeight() {
    if (flow.mode === "full") {
      setStep("qa");
      return;
    }
    saveRun();
  }
  function skipEndWeight() {
    patch({ endGrams: null });
    if (flow.mode === "full") {
      setStep("qa");
      return;
    }
    saveRun();
  }
  function skipQa() {
    patch({ qaSkipped: true });
    saveRun();
  }

  // ── Weigh + save ──
  async function saveRun() {
    if (!player) return;
    const f = flowRef.current;
    const grams = f.endGrams;
    setSaving(true);
    patch({ finalized: true });
    const stoppedAt = f.stoppedAt || Date.now();
    const minutes = f.activeMs / 60000;
    const localGpm = grams && minutes > 0 ? grams / minutes : null;
    const localYieldPct = f.mode === "full" && f.startGrams && grams && f.startGrams > 0
      ? round((grams / f.startGrams) * 100, 2)
      : null;

    const photoIds = f.mode === "full"
      ? { start_photo_id: f.startPhoto.id, end_photo_id: f.endPhoto.id, qa_photo_id: f.qaPhoto.id }
      : { start_photo_id: null, end_photo_id: null, qa_photo_id: null };

    // Multi-site orgs: tag the run with where this device is. Null = untagged.
    const siteId = getDeviceSite();
    const fields = {
      client_uuid: f.clientUuid,
      bag_id: f.bag?.id ?? "",
      player_id: player.id,
      strain_id: f.strain?.id ?? "",
      strain: f.strain?.id ? "" : f.strain?.name ?? "",
      mode: f.mode,
      started_at: f.startedAt,
      stopped_at: stoppedAt,
      paused_ms: f.pausedMs,
      active_ms: f.activeMs,
      start_grams: f.mode === "full" ? (f.startGrams ?? "") : "",
      end_grams: grams ?? "",
      trim_type: f.trimType,
      nug_size: f.nugSize,
      job_id: f.jobId ?? "",
      site_id: siteId ?? "",
      device_id: deviceId,
    };

    let queued = false;
    let job: Job | null = null;
    let serverRunId: number | null = null;
    if (navigator.onLine) {
      try {
        const res = await api.postForm("/api/runs", fields);
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        if (body.bag_link_status === "needs_review") toast("Work saved. The bag link needs manager review.", "err");
        job = body.job ?? null;
        serverRunId = typeof body.id === "number" ? body.id : null;
      } catch {
        queued = true;
      }
    } else {
      queued = true;
    }

    if (queued) {
      await queueRunOffline(
        {
          client_uuid: f.clientUuid,
        bag_id: f.bag?.id ?? null,
          mode: f.mode,
          started_at: f.startedAt,
          stopped_at: stoppedAt,
          active_ms: f.activeMs,
          paused_ms: f.pausedMs,
          start_grams: f.mode === "full" ? f.startGrams : null,
          end_grams: grams,
          strain_id: f.strain?.id ?? null,
          strain_name: f.strain?.name ?? null,
          trim_type: f.trimType,
          nug_size: f.nugSize,
          job_id: f.jobId,
          site_id: siteId,
          local_gpm: localGpm,
          ...photoIds,
        },
        player,
        deviceId,
      );
    } else if (f.mode === "full" && serverRunId !== null) {
      // Best-effort additive linkage — see lib/sync.ts's linkFullBatchPhotos
      // doc comment for why this can't just ride POST /api/runs's own payload.
      linkFullBatchPhotos(serverRunId, photoIds, deviceId).catch(() => {});
    }

    // Best-effort: mark the live session finished either way (M3's runs
    // endpoint doesn't yet do this itself for a live_run_id — see PROGRESS.md
    // T5's known gap). Harmless no-op if we're offline or never had a liveId.
    if (f.liveId) api.postJson(`/api/live/${f.liveId}/finish`, {}).catch(() => {});
    if (f.mode === "full") clearFlowPhotoIds(f.clientUuid);
    // M6 wave-2: this run is what the station clock-in was FOR — clock out
    // once it's saved (or queued; either way the worker is done at this
    // station for now). Best-effort like the /finish call above: a failed
    // clockout just leaves a stray open clock-in the NEXT clockin call
    // auto-closes anyway (line-routes.ts), so it's not worth retrying here.
    if (f.stationId !== null) clockOut();

    // M6 celebration verdict — computed from the engage/me snapshot cached
    // since trim start (BEFORE this run), entirely client-side (see
    // lib/engage.ts). Today-total/target-progress deltas are likewise added
    // locally rather than awaited from a refetch, so the done screen renders
    // immediately even if this run just got queued offline.
    const verdict = comparePb(
      { grams, active_ms: f.activeMs, trim_type: f.trimType, nug_size: f.nugSize },
      engageMe?.pbs,
      engageMe?.today.avg_gpm,
    );
    const tier = celebrationTier(verdict);
    // AI QA scoring is off in this environment (see ScaleCamOverlay/PhotoSlot
    // hints elsewhere) — "clean" here is the best available honest signal,
    // a captured-and-not-skipped QA photo, not an actual pass/fail score.
    // Swap for a real qa_score threshold once AI QA lands.
    const qaClean = f.mode === "full" && !f.qaSkipped && f.qaPhoto.id != null;
    const todayGramsBefore = engageMe?.today.grams ?? null;
    const todayGramsAfter = todayGramsBefore != null && grams != null ? todayGramsBefore + grams : null;
    const targetGrams = engageMe?.target.grams ?? null;
    const targetAfterPct = targetGrams && todayGramsAfter != null
      ? Math.min(100, (todayGramsAfter / targetGrams) * 100)
      : null;
    // Piece-rate "+$" — grams x the rate from the same pre-run snapshot, so
    // it renders instantly (and offline) like the verdict above; the home
    // ticker refetches the server's figure on the way back.
    const earnings = engageMe?.earnings ?? null;
    const earnedCents = earnings && grams != null ? Math.round(grams * earnings.rate_cents_per_g) : null;

    queryClient.invalidateQueries({ queryKey: ["runs-recent"] });
    queryClient.invalidateQueries({ queryKey: ["live-mine"] });
    queryClient.invalidateQueries({ queryKey: ["jobs"] }); // retained job chip (doAnother) shows fresh progress
    queryClient.invalidateQueries({ queryKey: ENGAGE_ME_KEY });
    setSaving(false);
    setDoneInfo({
      grams, gpm: localGpm, yieldPct: localYieldPct, queued, job, tier, verdict, qaClean,
      todayGramsAfter, targetGrams, targetAfterPct, percentileBand: engageMe?.percentile?.today_band ?? null,
      earnedCents, currency: earnings?.currency ?? null,
    });
    setStep("done");
  }

  // Keeps strain + job + station across runs so a worker parked on one line
  // station never re-picks — unless this run just CLOSED the job (nothing
  // left to trim toward). saveRun() already clocked the worker out (they're
  // genuinely off the line while the done screen is up, and "Home" from
  // there must not leave a stray open clock-in), so a retained station is
  // clocked back in here: station_gpm only counts grams from players with
  // an OPEN clock-in (line-routes.ts), so this is what re-attaches the
  // worker's recent grams to their station for the bottleneck view.
  function doAnother() {
    const f = flowRef.current;
    const keepJob = f.jobId !== null && doneInfo?.job?.status !== "done";
    setFlow((prev) => ({
      ...initialFlow(prev.mode),
      strain: prev.strain,
      ...(keepJob
        ? { jobId: prev.jobId, stationId: prev.stationId, stationName: prev.stationName, stationIsFinal: prev.stationIsFinal }
        : {}),
    }));
    if (keepJob && f.jobId !== null && f.stationId !== null) {
      clockIn(f.jobId, f.stationId).catch(() => toast("Could not clock back in — re-tap your station", "err"));
    }
    setDoneInfo(null);
    setShowConfetti(false);
    setStep("strain");
  }

  // ── Render ──
  const elapsedMs = flow.paused
    ? flow.pauseStart - flow.startedAt - flow.pausedMs
    : (flow.stopPendingAt || now) - flow.startedAt - flow.pausedMs;

  const strainChips: ChipItem<number>[] = (strains || []).map((s) => ({ key: s.id, label: s.name }));
  const trimTypeChips: ChipItem<string>[] = workTypes?.length
    ? workTypes.map((t) => ({ key: t.slug, label: t.label }))
    : TRIM_TYPES.map((t) => ({ key: t, label: t.replace("_", " ") }));
  const nugSizeChips: ChipItem<string>[] = NUG_SIZES.map((n) => ({ key: n, label: n[0].toUpperCase() + n.slice(1) }));
  const left = resumeLeftMs();

  // M6: static snapshot pill (engage/me fetched once at trim start) — a
  // falsy avg_gpm means "no baseline yet" (same guard as paceVsAvgPct uses),
  // not "dead even". The scale-cam overlay recomputes the live equivalent
  // every frame instead, see the avgGpm prop passed to it below.
  const timerPaceVsAvg = engageMe?.today.avg_gpm ? engageMe.today.vs_avg_pct : null;

  return (
    <>
      {showConfetti && (
        <ConfettiBurst
          count={doneInfo?.tier === "pb" ? 90 : 45}
          onDone={() => setShowConfetti(false)}
        />
      )}
      <header className="top-bar">
        <div className="brand">
          <AppMark />{flow.mode === "full" ? "FULL BATCH" : "QUICK"}<small>{player?.name}</small>
        </div>
        <div className="top-actions">
          <button
            type="button" className="link-btn"
            onClick={() => {
              if (flow.stationId !== null) clockOut();
              navigate({ to: "/" });
            }}
          >
            Exit
          </button>
        </div>
      </header>
      <main className="page">
        {flow.bag && <div className="panel"><strong>Bag {flow.bag.label}</strong><p className="small muted">{flow.bag.strainName} · material is weighed and reviewed in Room</p></div>}
        {step === "strain" && <BagAssignment onChoose={bag => { patch({ bag, strain: { id: bag.strainId, name: bag.strainName } }); }} />}
        {step === "strain" && (
          <section className="step active" data-testid="step-strain">
            <div className="step-head">
              <div className="step-num">{stepNumbers.strain}</div>
              <div>
                <div className="step-title">Which strain?</div>
                <div className="step-sub">Most recent at top.</div>
              </div>
            </div>
            <div className="panel">
              {openJobs.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div className="field-label">Active jobs — tap to trim toward one</div>
                  <ChipGrid
                    tall
                    items={openJobs.map((j) => ({
                      key: j.id, label: j.name, note: `${j.pct}% · ${fmtWeight(Math.round(j.remaining_grams), units)} to go`,
                    }))}
                    activeKey={flow.jobId ?? undefined}
                    onSelect={(id) => {
                      const j = openJobs.find((x) => x.id === id);
                      if (j) pickJob(j);
                    }}
                  />
                </div>
              )}
              {needsStation && (
                <div style={{ marginBottom: 14 }} data-testid="station-picker">
                  <div className="field-label">Pick your station</div>
                  <ChipGrid
                    items={(lineStations.data || []).map((s, i): ChipItem<number> => ({
                      key: s.id, label: s.name, note: s.is_final ? "weigh-out" : undefined,
                      testId: `station-chip-${i}`,
                    }))}
                    activeKey={flow.stationId ?? undefined}
                    onSelect={(id) => {
                      const s = lineStations.data?.find((x) => x.id === id);
                      if (s) switchToStation(s);
                    }}
                  />
                </div>
              )}
              <div className="field-label">Trim style</div>
              <ChipGrid
                items={trimTypeChips}
                activeKey={flow.trimType}
                onSelect={(t) => {
                  patch({ trimType: t });
                  setTrimType(t);
                }}
              />
              <div className="field-label" style={{ marginTop: 10 }}>
                Average nug size <span className="muted small">(fair-speed grading)</span>
              </div>
              <ChipGrid
                items={nugSizeChips}
                activeKey={flow.nugSize}
                onSelect={(n) => {
                  patch({ nugSize: n });
                  setNugSize(n);
                }}
              />
              <div className="field-label" style={{ marginTop: 14 }}>Strain — tapping starts you off</div>
              <ChipGrid
                items={strainChips}
                activeKey={flow.strain?.id ?? undefined}
                onSelect={(id) => {
                  const s = strains?.find((x) => x.id === id);
                  if (s) pickStrain({ id: s.id, name: s.name });
                }}
                trailing={
                  <button type="button" className="chip chip-other" onClick={addOtherStrain}>+ Other…</button>
                }
              />
            </div>
          </section>
        )}

        {step === "startweight" && (
          <section className="step active" data-testid="step-startweight">
            <div className="step-head">
              <div className="step-num">{stepNumbers.startweight}</div>
              <div>
                <div className="step-title">Start weight</div>
                <div className="step-sub">Put the untrimmed material on the scale.</div>
              </div>
            </div>
            <div className="panel">
              <PhotoSlot
                testId="photo-start"
                label="Tap to take photo of scale"
                hint={units === "imperial" ? "Enter the ounces below — AI reading is off in this environment." : "Enter the grams below — AI reading is off in this environment."}
                field={flow.startPhoto}
                onCapture={(f) => capturePhoto("start", f)}
                onRetry={() => retryPhoto("start")}
              />
              <div className="field" style={{ marginTop: 14 }}>
                <label className="field-label" htmlFor="start-grams">{units === "imperial" ? "Ounces (oz)" : "Grams"}</label>
                <input
                  id="start-grams"
                  data-testid="start-grams"
                  className="big-input"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  placeholder={units === "imperial" ? "0.00 oz" : "0.0"}
                  value={entryValue(ozText.start, flow.startGrams)}
                  onChange={(e) => entryChange("start", e.target.value)}
                />
              </div>
              <div className="btn-row">
                <button type="button" className="btn btn-ghost" onClick={() => setStep("strain")}>Back</button>
                <button
                  type="button" className="btn btn-primary" data-testid="start-trim-btn"
                  onClick={() => setHygieneOpen(true)}
                >
                  Start trimming
                </button>
              </div>
            </div>
          </section>
        )}

        {step === "trim" && (
          <section className="step active" data-testid="step-trim">
            <div className="step-head">
              <div className="step-num">{stepNumbers.trim}</div>
              <div>
                <div className="step-title">Trim</div>
                <div className="step-sub">Timer is running — synced live.</div>
              </div>
            </div>
            <div className={`timer-card${flow.paused ? " paused" : ""}`}>
              <div className="timer-display" data-testid="timer-display">{formatTimer(Math.max(0, elapsedMs))}</div>
              <div className="timer-sub">{flow.paused ? "Paused" : "Elapsed"}</div>
            </div>
            {timerPaceVsAvg != null && (
              <div
                className={`pace-pill ${timerPaceVsAvg >= 0 ? "up" : "down"}`}
                data-testid="pace-pill"
                data-direction={timerPaceVsAvg >= 0 ? "up" : "down"}
              >
                {timerPaceVsAvg >= 0 ? "▲" : "▼"} {Math.abs(timerPaceVsAvg)}% vs your avg
              </div>
            )}
            <div className="btn-row">
              <button type="button" className={flow.paused ? "btn btn-primary" : "btn btn-warn"} onClick={togglePause}>
                {flow.paused ? "Resume" : "Pause"}
              </button>
              <button type="button" className="btn btn-stop" data-testid="stop-timer" onClick={() => setStopModalOpen(true)}>
                Stop
              </button>
            </div>
            <button
              type="button"
              className="btn btn-ghost scale-cam-btn"
              data-testid="open-scale-cam"
              disabled={!flow.liveId}
              title={flow.liveId ? undefined : "Needs a connection to start"}
              onClick={() => setScaleCamOpen(true)}
            >
              Live scale mode — watch your {units === "imperial" ? "oz/hr" : "g/min"}
            </button>
            <div className="panel" style={{ marginTop: 14 }}>
              <div className="row gap-lg">
                <div>
                  <div className="field-label">Trimmer</div>
                  <div className="mono">{player?.name}</div>
                </div>
                <div>
                  <div className="field-label">Strain</div>
                  <div className="mono">{flow.strain?.name || "—"}</div>
                </div>
                {flow.mode === "full" && (
                  <div>
                    <div className="field-label">Start</div>
                    <div className="mono">{flow.startGrams != null ? fmtWeight(flow.startGrams, units) : "—"}</div>
                  </div>
                )}
                {flow.stationId !== null && (
                  <div>
                    <div className="field-label">Station</div>
                    <div className="mono" data-testid="current-station-name">
                      {flow.stationName}{flow.stationIsFinal ? " (weigh-out)" : ""}
                    </div>
                  </div>
                )}
              </div>
              {flow.stationId !== null && pool && (
                <div className="muted small" style={{ marginTop: 8 }} data-testid="station-pool">
                  Pool {formatMoney(pool.total_cents, pool.currency)}
                  {myShare ? ` · your share ${formatMoney(myShare.cents, pool.currency)}` : ""}
                </div>
              )}
              {flow.stationId !== null && (
                <button
                  type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}
                  data-testid="switch-station-btn" onClick={() => setStationSwitchOpen(true)}
                >
                  Switch station
                </button>
              )}
            </div>
          </section>
        )}

        {step === "endweight" && (
          <section className="step active" data-testid="step-endweight">
            {flow.stopPendingAt > 0 && !flow.finalized && left > 0 && (
              <div className={`panel resume-bar${left <= RESUME_WARN_MS ? " urgent" : ""}`}>
                <div>
                  <div className="resume-title">Not done? Resume this session</div>
                  <div className="small muted">
                    Same timer continues —{" "}
                    <span className="mono">
                      {Math.floor(left / 60000)}:{String(Math.floor((left % 60000) / 1000)).padStart(2, "0")}
                    </span>{" "}
                    left before it locks in.
                  </div>
                </div>
                <button type="button" className="btn btn-primary" onClick={resumeTrimming}>Resume</button>
              </div>
            )}
            <div className="step-head">
              <div className="step-num">{stepNumbers.endweight}</div>
              <div>
                <div className="step-title">{flow.mode === "full" ? "End weight" : "Weigh it"}</div>
                <div className="step-sub">Bag the buds first — put trimmed product on the scale.</div>
                {flow.stationId !== null && !flow.stationIsFinal && (
                  <div className="small muted" data-testid="mid-line-weight-note">
                    Weights land at weigh-out — skip if you&rsquo;re not weighing product at this station.
                  </div>
                )}
              </div>
            </div>
            <div className="panel">
              {flow.mode === "full" && (
                <PhotoSlot
                  testId="photo-end"
                  label="Tap to take photo of scale"
                  hint={units === "imperial" ? "Enter the ounces below — AI reading is off in this environment." : "Enter the grams below — AI reading is off in this environment."}
                  field={flow.endPhoto}
                  onCapture={(f) => capturePhoto("end", f)}
                  onRetry={() => retryPhoto("end")}
                />
              )}
              <div className="field" style={flow.mode === "full" ? { marginTop: 14 } : undefined}>
                <label className="field-label" htmlFor="end-grams">{units === "imperial" ? "Ounces (oz)" : "Grams"}</label>
                <input
                  id="end-grams"
                  data-testid="end-grams"
                  className="big-input"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  placeholder={units === "imperial" ? "0.00 oz" : "0.0"}
                  value={entryValue(ozText.end, flow.endGrams)}
                  onChange={(e) => entryChange("end", e.target.value)}
                />
              </div>
              <div className="btn-row">
                <button type="button" className="btn btn-ghost" onClick={skipEndWeight} disabled={saving}>Skip</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid={flow.mode === "full" ? "continue-to-qa" : "save-run"}
                  disabled={saving || !flow.endGrams || flow.endGrams <= 0}
                  onClick={afterEndWeight}
                >
                  {flow.mode === "full" ? "QA photo" : "Save"}
                </button>
              </div>
            </div>
          </section>
        )}

        {step === "qa" && (
          <section className="step active" data-testid="step-qa">
            <div className="step-head">
              <div className="step-num">{stepNumbers.qa}</div>
              <div>
                <div className="step-title">QA photo</div>
                <div className="step-sub">Lay trimmed product on a clean surface.</div>
              </div>
            </div>
            <div className="panel">
              <PhotoSlot
                testId="photo-qa"
                label="Tap to take QA photo"
                hint="AI QA scoring is off in this environment — photo is saved for manual review."
                field={flow.qaPhoto}
                onCapture={(f) => capturePhoto("qa", f)}
                onRetry={() => retryPhoto("qa")}
              />
              <div className="btn-row" style={{ marginTop: 14 }}>
                <button type="button" className="btn btn-ghost" data-testid="skip-qa" onClick={skipQa} disabled={saving}>
                  Skip QA
                </button>
                <button type="button" className="btn btn-primary" data-testid="save-run" onClick={saveRun} disabled={saving}>
                  Save run
                </button>
              </div>
            </div>
          </section>
        )}

        {step === "done" && (
          <section className="step active" data-testid="step-done">
            <div className="panel center">
              <h2 style={{ border: 0, justifyContent: "center" }}>Saved</h2>

              {/* Job-completion ceremony stays PRIMARY (right under the
                  heading, unchanged styling) when a run just closed a job —
                  the M6 celebration below always composes UNDER it, never
                  replaces it. With no job (or an in-progress one, shown
                  further down with the rest of the stats) the celebration
                  takes this top slot instead. */}
              {doneInfo?.job?.status === "done" && (
                <div style={{ marginTop: 10 }} data-testid="job-ceremony">
                  <b>
                    &quot;{doneInfo.job.name}&quot; is DONE — {fmtWeight(Math.round(doneInfo.job.done_grams), units)} of{" "}
                    {fmtWeight(Math.round(doneInfo.job.target_grams), units)}.
                  </b>
                </div>
              )}

              {doneInfo && (
                <div
                  className={`celebration celebration-${doneInfo.tier}`}
                  data-testid="celebration"
                  data-tier={doneInfo.tier}
                  style={{ marginTop: 10 }}
                >
                  <div className="celebration-copy" data-testid="celebration-copy">
                    {celebrationCopy(doneInfo, flow.trimType, flow.nugSize, units)}
                  </div>
                  {doneInfo.qaClean && (
                    <div className="celebration-qa" data-testid="qa-clean-line">clean QA ✓</div>
                  )}
                  {doneInfo.tier === "solid" && doneInfo.percentileBand && (
                    <div className="small muted" data-testid="percentile-band">
                      Today&rsquo;s pace: {doneInfo.percentileBand}
                    </div>
                  )}
                  {doneInfo.targetGrams != null && doneInfo.todayGramsAfter != null && (
                    <div className="small muted" data-testid="target-delta">
                      Today: {Math.round(doneInfo.todayGramsAfter)}g of {Math.round(doneInfo.targetGrams)}g
                      {doneInfo.targetAfterPct != null ? ` (${Math.round(doneInfo.targetAfterPct)}%)` : ""}
                      {doneInfo.targetAfterPct != null && doneInfo.targetAfterPct >= 100 ? " — target met!" : ""}
                    </div>
                  )}
                </div>
              )}

              <div className="muted" style={{ marginTop: 10 }}>
                <div>{player?.name} · {flow.strain?.name || ""}</div>
                <div>
                  {doneInfo?.grams ? fmtWeight(doneInfo.grams, units) : ""}
                  {doneInfo?.gpm ? ` · ${fmtRate(doneInfo.gpm, units, 1)}` : ""}
                </div>
                {doneInfo?.earnedCents != null && doneInfo.currency && (
                  <div className="earned-line" data-testid="earned-line">
                    +{formatMoney(doneInfo.earnedCents, doneInfo.currency)}
                  </div>
                )}
                {doneInfo?.yieldPct != null && <div>{doneInfo.yieldPct.toFixed(1)}% yield</div>}
                <div>
                  {formatTimer(flow.activeMs)} active
                  {flow.pausedMs ? ` (+ ${formatTimer(flow.pausedMs)} paused)` : ""}
                </div>
                {doneInfo?.queued && (
                  <div style={{ color: "var(--warning)", marginTop: 6 }} data-testid="queued-note">
                    Queued — will sync when online
                  </div>
                )}
                {doneInfo?.job && doneInfo.job.status !== "done" && (
                  <div style={{ marginTop: 8 }}>
                    Job &quot;{doneInfo.job.name}&quot;: <b>{doneInfo.job.pct}%</b> ·{" "}
                    {fmtWeight(Math.round(doneInfo.job.remaining_grams), units)} left
                  </div>
                )}
              </div>
              <div className="btn-row" style={{ marginTop: 20 }}>
                <button type="button" className="btn btn-ghost" onClick={() => navigate({ to: "/" })}>Home</button>
                <button type="button" className="btn btn-primary" onClick={doAnother}>Do another</button>
              </div>
            </div>
          </section>
        )}
      </main>
      {flow.bag ? <FacilityReadiness batchId={flow.bag.batchId} open={hygieneOpen} onConfirm={beginTrim} onCancel={() => setHygieneOpen(false)} /> : <HygieneModal open={hygieneOpen} onConfirm={beginTrim} onCancel={() => setHygieneOpen(false)} />}
      <StopModal open={stopModalOpen} onConfirm={confirmStop} onCancel={() => setStopModalOpen(false)} />
      {stationSwitchOpen && (
        <Modal title="Switch station" onClose={() => setStationSwitchOpen(false)}>
          {/* No final-station indicator here (unlike the strain-step picker
              below) — with only 2-3 chips the modal's narrower grid gives
              each one too little width for label + a "weigh-out" note
              without truncating, and the worker's already seen which
              station is final twice by this point: at the initial pick, and
              in the trim step's "Station" field just before this opened. */}
          <ChipGrid
            items={(lineStations.data || []).map((s, i): ChipItem<number> => ({
              key: s.id, label: s.name, testId: `switch-station-chip-${i}`,
            }))}
            activeKey={flow.stationId ?? undefined}
            onSelect={(id) => {
              const s = lineStations.data?.find((x) => x.id === id);
              if (s) switchToStation(s);
              setStationSwitchOpen(false);
            }}
          />
          {pool && (
            <div className="muted small" style={{ marginTop: 12 }} data-testid="switch-pool">
              Job pool {formatMoney(pool.total_cents, pool.currency)} split by station-hours
              {myShare ? ` · your share ${formatMoney(myShare.cents, pool.currency)}` : ""}
            </div>
          )}
        </Modal>
      )}
      {scaleCamOpen && flow.liveId && (
        <ScaleCamOverlay
          liveId={flow.liveId}
          avgGpm={engageMe?.today.avg_gpm ?? null}
          onClose={() => setScaleCamOpen(false)}
          onCancelled={() => {
            setScaleCamOpen(false);
            onCancelledByAdmin("admin");
          }}
        />
      )}
    </>
  );
}
